import Alpine from 'alpinejs'
import UAParser from 'ua-parser-js'
import '/src/js/ext/string.js'
import screenfull from 'screenfull'

window.Alpine = Alpine

const userAgent = new UAParser().getResult()

Alpine.store('system', {
  browser: {
    name: 'unknown',
    version: '',
    get info() {
      return `${this.name} ${this.version}`.trim()
    },
  },
  os: {
    name: 'unknown',
    version: '',
    get info() {
      return `${this.name} ${this.version}`.trim()
    },
  },
  platform: 'desktop',
  viewport: {
    width: window.innerWidth,
    height: window.innerHeight,
  },

  init() {
    this.detectBrowser(userAgent)
    this.detectOS(userAgent)
    this.detectPlatform(userAgent)
    this.detectViewport()

    window.addEventListener('resize', () => this.detectViewport())
  },

  detectBrowser(userAgent) {
    const browser = userAgent.browser

    if (typeof browser.name === 'undefined' || browser.name === '') return

    this.browser.name = browser.name
    this.browser.version = browser.version
  },

  detectOS(userAgent) {
    const os = userAgent.os

    if (typeof os.name === 'undefined' || os.name === '') return

    this.os.name = os.name
    this.os.version = os.version
  },

  detectPlatform(userAgent) {
    const device = userAgent.device.type

    if (typeof device === 'undefined') return

    this.platform = device
  },

  detectViewport() {
    this.viewport.width = window.innerWidth
    this.viewport.height = window.innerHeight
  },
})

Alpine.data('fullskrin', () => ({
  tab: 'text',
  text: '',
  images: [],
  hasImages: false,
  wakeLock: null,
  slideshowInterval: null,
  bgColors: {
    cyan: '#0891b2',
    amber: '#f59e0b',
    teal: '#14b8a6',
    indigo: '#4f46e5',
    slate: '#1e293b',
    rose: '#fb7185',
  },
  bgColor: '#0891b2', // Default color
  currentTime: '',
  selectedTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  timezones: Intl.supportedValuesOf('timeZone').sort(),
  selectedTimeFormat: '12',
  timerMinutes: '05',
  timerSeconds: '00',
  countdownTotalSeconds: 0,
  stopwatchTime: '00:00:00',
  stopwatchRunning: false,
  stopwatchStartTime: null,
  stopwatchInterval: null,
  selectedClockFeature: 'time',
  toastMessage: '',
  toastVisible: false,
  eventListenersAdded: false,
  previousClientWidth: null,

  get dataLines() {
    const lines = (this.text.match(/\n/g) || []).length + 1
    return Math.max(3, lines + 2)
  },

  get height() {
    // Assuming approximately 1.5rem per row for text-3xl
    return `${this.dataLines * 1.5}rem`
  },

  showToast(message) {
    this.toastMessage = message
    this.toastVisible = true
    setTimeout(() => (this.toastVisible = false), 3000)
  },

  focusVisibleInput() {
    let element = null

    if (this.tab === 'text') {
      element = document.getElementById('text')
    } else if (this.tab === 'image' && !this.hasImages) {
      element = document.getElementById('image-url')
    }

    if (!element) return

    element.select()
  },

  handleFileUpload(event) {
    const wasEmpty = this.images.length === 0
    const files = Array.from(event.target.files).filter((f) =>
      f.type.startsWith('image/'),
    )
    files.forEach((file) => {
      const url = URL.createObjectURL(file)
      this.images.push(url)
    })
    this.hasImages = this.images.length > 0
    if (wasEmpty && this.images.length > 0) {
      this.showToast('✨ Keep adding images for a slideshow')
    }
  },

  removeImage(index) {
    const url = this.images[index]
    URL.revokeObjectURL(url)
    this.images.splice(index, 1)
    this.hasImages = this.images.length > 0
  },

  clearAllImages() {
    this.images.forEach((url) => {
      if (url.startsWith('blob:')) URL.revokeObjectURL(url)
    })
    this.images = []
    this.hasImages = false
  },

  exitFullscreenHandler(e) {
    if (e.key === 'Enter') screenfull.exit()
  },

  adjustTimer(delta) {
    if (this.fullscreenTimerInterval) {
      // If countdown is running, add to the current total seconds
      this.countdownTotalSeconds += delta * 60
      if (this.countdownTotalSeconds < 0) {
        this.countdownTotalSeconds =
          ((this.countdownTotalSeconds % 60) + 60) % 60
      }
      // Clamp to max
      this.countdownTotalSeconds = Math.min(
        99 * 60 + 59,
        this.countdownTotalSeconds,
      )
      // Update minutes and seconds from total
      this.timerMinutes = Math.floor(this.countdownTotalSeconds / 60)
        .toString()
        .padStart(2, '0')
      this.timerSeconds = (this.countdownTotalSeconds % 60)
        .toString()
        .padStart(2, '0')
      this.updateTimerFields()
    } else {
      // If not running, adjust the set time
      let totalSeconds =
        (parseInt(this.timerMinutes) || 0) * 60 +
        (parseInt(this.timerSeconds) || 0) +
        delta * 60
      if (totalSeconds < 0) {
        totalSeconds = parseInt(this.timerSeconds) || 0
      }
      // Clamp to max
      totalSeconds = Math.min(99 * 60 + 59, totalSeconds)
      this.timerMinutes = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, '0')
      this.timerSeconds = (totalSeconds % 60).toString().padStart(2, '0')
      this.updateTimerFields()
    }
  },

  updateTimerFields() {
    if (this.$refs.minutesInput)
      this.$refs.minutesInput.value = this.timerMinutes
    if (this.$refs.secondsInput)
      this.$refs.secondsInput.value = this.timerSeconds
  },

  updateCurrentTime() {
    const now = new Date()
    const options = {
      timeZone: this.selectedTimezone,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    }
    if (this.selectedTimeFormat === '12') {
      options.hour12 = true
    } else {
      options.hour12 = false
    }
    this.currentTime = now.toLocaleString('en-US', options)
  },

  updateStopwatchTime() {
    if (!this.stopwatchRunning || !this.stopwatchStartTime) return

    const elapsedMs = Date.now() - this.stopwatchStartTime
    const totalSeconds = Math.floor(elapsedMs / 1000)
    const centiseconds = Math.floor((elapsedMs % 1000) / 10)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    this.stopwatchTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}:${centiseconds.toString().padStart(2, '0')}`
  },

  startStopwatch() {
    if (!this.stopwatchRunning) {
      this.stopwatchRunning = true
      this.stopwatchStartTime =
        Date.now() - (this.stopwatchRunning ? this.getElapsedMs() : 0)
      this.stopwatchInterval = setInterval(() => this.updateStopwatchTime(), 10)
    }
  },

  stopStopwatch() {
    this.stopwatchRunning = false
    if (this.stopwatchInterval) {
      clearInterval(this.stopwatchInterval)
      this.stopwatchInterval = null
    }
  },

  resetStopwatch() {
    this.stopStopwatch()
    this.stopwatchTime = '00:00:00'
    this.stopwatchStartTime = null
  },

  getElapsedMs() {
    if (!this.stopwatchStartTime) return 0
    const currentTime = Date.now()
    const elapsedMs = currentTime - this.stopwatchStartTime
    return elapsedMs
  },

  handleMinutesKeydown(event) {
    if (this.selectedClockFeature !== 'countdown') return

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      this.adjustTimer(1)
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.adjustTimer(-1)
    } else if (event.key === 'ArrowRight') {
      // If at the right edge of minutes input, move to seconds
      if (event.target.selectionStart === 2) {
        event.preventDefault()
        this.$refs.secondsInput.focus()
        this.$refs.secondsInput.setSelectionRange(0, 0)
      }
    } else if (event.key >= '0' && event.key <= '9') {
      event.preventDefault()
      const newVal = (this.timerMinutes + event.key).slice(-2).padStart(2, '0')
      this.timerMinutes = newVal
      this.$refs.minutesInput.value = newVal
    } else if (event.key === 'Backspace') {
      event.preventDefault()
      this.timerMinutes = this.timerMinutes.slice(0, -1).padStart(2, '0')
      this.$refs.minutesInput.value = this.timerMinutes
    }
  },

  handleSecondsKeydown(event) {
    if (this.selectedClockFeature !== 'countdown') return

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      const sec = parseInt(this.timerSeconds) || 0
      if (sec < 59) {
        const newSec = sec + 1
        this.timerSeconds = newSec.toString().padStart(2, '0')
        this.$refs.secondsInput.value = this.timerSeconds
        if (this.fullscreenTimerInterval) {
          this.countdownTotalSeconds =
            (parseInt(this.timerMinutes) || 0) * 60 +
            parseInt(this.timerSeconds)
        }
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      const sec = parseInt(this.timerSeconds) || 0
      if (sec > 0) {
        const newSec = sec - 1
        this.timerSeconds = newSec.toString().padStart(2, '0')
        this.$refs.secondsInput.value = this.timerSeconds
        if (this.fullscreenTimerInterval) {
          this.countdownTotalSeconds =
            (parseInt(this.timerMinutes) || 0) * 60 +
            parseInt(this.timerSeconds)
        }
      }
    } else if (event.key === 'ArrowLeft') {
      // If at the left edge of seconds input, move to minutes
      if (event.target.selectionStart === 0) {
        event.preventDefault()
        this.$refs.minutesInput.focus()
        this.$refs.minutesInput.setSelectionRange(2, 2)
      }
    } else if (event.key >= '0' && event.key <= '9') {
      event.preventDefault()
      let newVal = (this.timerSeconds + event.key).slice(-2)
      // Validate seconds
      const num = parseInt(newVal)
      if (num > 59) newVal = '59'
      this.timerSeconds = newVal.padStart(2, '0')
      this.$refs.secondsInput.value = this.timerSeconds
      if (this.fullscreenTimerInterval) {
        this.countdownTotalSeconds =
          (parseInt(this.timerMinutes) || 0) * 60 + parseInt(this.timerSeconds)
      }
    } else if (event.key === 'Backspace') {
      event.preventDefault()
      if (this.timerSeconds === '00') {
        this.$refs.minutesInput.focus()
      } else {
        this.timerSeconds = this.timerSeconds.slice(0, -1).padStart(2, '0')
        this.$refs.secondsInput.value = this.timerSeconds
        if (this.fullscreenTimerInterval) {
          this.countdownTotalSeconds =
            (parseInt(this.timerMinutes) || 0) * 60 +
            parseInt(this.timerSeconds)
        }
      }
    }
  },

  startColorTerrorita(fullscreenContent) {
    fullscreenContent.innerHTML = `
      <div class="text-9xl font-mono font-bold shake">
        00:00
      </div>
    `
  },

  selectClockFeature(feature) {
    if (this.selectedClockFeature === feature) return

    // Clear intervals for the previous feature
    if (this.selectedClockFeature === 'time') {
      if (this.fullscreenClockInterval) {
        clearInterval(this.fullscreenClockInterval)
        this.fullscreenClockInterval = null
      }
    } else if (this.selectedClockFeature === 'stopwatch') {
      if (this.fullscreenStopwatchInterval) {
        clearInterval(this.fullscreenStopwatchInterval)
        this.fullscreenStopwatchInterval = null
      }
    }

    this.selectedClockFeature = feature

    // Update time immediately when switching features
    this.updateCurrentTime()

    if (feature === 'timer') {
      this.updateTimerFields()
    }
  },

  init() {
    this.focusVisibleInput()

    this.updateCurrentTime() // Initial update
    setInterval(() => this.updateCurrentTime(), 1000) // Update every second

    this.updateTimerFields()

    // Populate timezone select
    this.$nextTick(() => {
      const select = document.querySelector(
        'select[x-model="selectedTimezone"]',
      )
      if (select && select.children.length === 0) {
        const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone

        // Common group
        const commonGroup = document.createElement('optgroup')
        commonGroup.label = 'Common'

        // Local
        const localOption = document.createElement('option')
        localOption.value = localTz
        localOption.textContent = 'Local'
        commonGroup.appendChild(localOption)

        // UTC
        const utcOption = document.createElement('option')
        utcOption.value = 'UTC'
        utcOption.textContent = 'UTC'
        commonGroup.appendChild(utcOption)

        select.appendChild(commonGroup)

        // All timezones group
        const allGroup = document.createElement('optgroup')
        allGroup.label = 'All Timezones'

        this.timezones.forEach((tz) => {
          if (tz !== 'UTC' && tz !== localTz) {
            const city = tz.split('/').pop().replace(/_/g, ' ')
            const formatter = new Intl.DateTimeFormat('en-US', {
              timeZone: tz,
              timeZoneName: 'longOffset',
            })
            const formatted = formatter.format(new Date())
            const offset = formatted.split(' ').pop()
            const text = `${city} (${offset})`
            const option = document.createElement('option')
            option.value = tz
            option.textContent = text
            allGroup.appendChild(option)
          }
        })

        select.appendChild(allGroup)

        // Ensure the correct option is selected
        select.value = this.selectedTimezone
      }
    })

    this.$watch('tab', () => {
      setTimeout(() => {
        this.focusVisibleInput()
        if (this.tab === 'clock') {
          // Focus the active feature button
          setTimeout(() => {
            const buttonId = `${this.selectedClockFeature}-btn`
            const button = document.getElementById(buttonId)
            if (button) {
              const originalTabIndex = button.tabIndex
              button.tabIndex = 0
              button.focus()
              setTimeout(() => (button.tabIndex = originalTabIndex), 100)
            }
          }, 0)
        }
      }, 100)
    })

    // Handle clipboard paste for images on image tab
    document.addEventListener('paste', (e) => {
      if (this.tab === 'image' && e.clipboardData && e.clipboardData.items) {
        const wasEmpty = this.images.length === 0
        for (const item of e.clipboardData.items) {
          if (item.type.startsWith('image/')) {
            e.preventDefault()
            const file = item.getAsFile()
            const url = URL.createObjectURL(file)
            this.images.push(url)
            this.hasImages = true
            if (wasEmpty) {
              this.showToast('✨ Keep adding images for a slideshow')
            }
            break
          }
        }
      }
    })

    // Watch for time format and timezone changes to update immediately
    this.$watch('selectedTimeFormat', () => this.updateCurrentTime())
    this.$watch('selectedTimezone', () => this.updateCurrentTime())

    // Re-acquire wake lock when tab becomes visible again
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && screenfull.isFullscreen) {
        this.acquireWakeLock()
      }
    })
  },

  async acquireWakeLock() {
    if ('wakeLock' in navigator) {
      try {
        this.wakeLock = await navigator.wakeLock.request('screen')
        this.wakeLock.addEventListener('release', () => {
          this.wakeLock = null
        })
      } catch {
        // Wake lock request failed (e.g. low battery)
      }
    }
  },

  async releaseWakeLock() {
    if (this.wakeLock) {
      await this.wakeLock.release()
      this.wakeLock = null
    }
  },

  goFullscreen() {
    if (this.tab === 'text' && !this.text.trim()) {
      this.showToast('⚠️ Type some text first')
      return
    }
    if (this.tab === 'image' && this.images.length === 0) {
      this.showToast('⚠️ Upload an image first')
      return
    }

    // Clear fullscreen intervals for display updates, but keep timer and stopwatch running
    if (this.fullscreenClockInterval) {
      clearInterval(this.fullscreenClockInterval)
      this.fullscreenClockInterval = null
    }
    if (this.fullscreenStopwatchInterval) {
      clearInterval(this.fullscreenStopwatchInterval)
      this.fullscreenStopwatchInterval = null
    }

    const fullscreenWrapper = document.getElementById('fullscreen-wrapper')
    const fullscreenContent = document.getElementById('fullscreen-content')

    fullscreenContent.style.backgroundColor = this.bgColor
    fullscreenContent.className =
      'text-white flex flex-col items-center justify-center text-center'

    if (this.tab === 'text') {
      const width = Alpine.store('system').viewport.width
      const initialPx = Math.min(160, Math.floor(width * 0.8))

      // Split text into paragraphs by double newlines, trim each
      const paragraphs = this.text
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter((p) => p)

      // Generate HTML for each paragraph with initial large font size
      fullscreenContent.innerHTML = paragraphs
        .map(
          (paragraph) => `
        <p
          class="p-4 whitespace-pre-line text-center leading-tight"
          style="font-size: ${initialPx}px"
        >${paragraph}</p>
      `,
        )
        .join('')

      this.previousClientWidth = fullscreenContent.clientWidth

      // Function to check and adjust for overflow
      const checkOverflow = () => {
        setTimeout(() => {
          const currentClientWidth = fullscreenContent.clientWidth
          const viewportShrank =
            this.previousClientWidth &&
            currentClientWidth < this.previousClientWidth
          this.previousClientWidth = currentClientWidth

          const maxPx = Math.min(
            160,
            Math.floor(fullscreenContent.clientWidth * 0.8),
          )
          const pElements = fullscreenContent.querySelectorAll('p')
          if (pElements.length === 0) return
          let fontSizePx = parseFloat(getComputedStyle(pElements[0]).fontSize)

          if (
            fullscreenContent.scrollWidth > fullscreenContent.clientWidth ||
            fullscreenContent.scrollHeight > fullscreenContent.clientHeight
          ) {
            while (
              (fullscreenContent.scrollWidth > fullscreenContent.clientWidth ||
                fullscreenContent.scrollHeight >
                  fullscreenContent.clientHeight) &&
              fontSizePx > 8
            ) {
              fontSizePx = Math.floor(fontSizePx * 0.9)
              pElements.forEach((p) => (p.style.fontSize = `${fontSizePx}px`))
            }
          } else if (fontSizePx < maxPx && !viewportShrank) {
            setTimeout(() => {
              const updatedMaxPx = Math.min(
                160,
                Math.floor(fullscreenContent.clientWidth * 0.8),
              )
              if (fontSizePx < updatedMaxPx) {
                while (fontSizePx < updatedMaxPx) {
                  const previousPx = fontSizePx
                  fontSizePx = Math.floor(fontSizePx / 0.9)
                  if (fontSizePx > updatedMaxPx) fontSizePx = updatedMaxPx
                  pElements.forEach(
                    (p) => (p.style.fontSize = `${fontSizePx}px`),
                  )
                  if (
                    fullscreenContent.scrollWidth >
                      fullscreenContent.clientWidth ||
                    fullscreenContent.scrollHeight >
                      fullscreenContent.clientHeight
                  ) {
                    pElements.forEach(
                      (p) => (p.style.fontSize = `${previousPx}px`),
                    )
                    fontSizePx = previousPx
                    break
                  }
                }
              }
            }, 100)
          }
        }, 0)
      }

      // Store checkOverflow for use in event listeners
      this.checkOverflow = checkOverflow
    } else if (this.tab === 'image') {
      if (this.images.length === 1) {
        fullscreenContent.innerHTML = `
          <img
            src="${this.images[0]}"
            class="object-contain max-w-full"
            alt="Fullscreen image"
          />
        `
      } else if (this.images.length > 1) {
        let index = 0
        fullscreenContent.innerHTML = `
          <img
            id="slideshow-img"
            src="${this.images[0]}"
            class="object-contain max-w-full transition-opacity duration-500"
            alt="Slideshow"
          />
        `
        const imgEl = document.getElementById('slideshow-img')
        imgEl.style.opacity = 1
        this.slideshowInterval = setInterval(() => {
          imgEl.style.opacity = 0
          setTimeout(() => {
            index = (index + 1) % this.images.length
            imgEl.src = this.images[index]
            imgEl.style.opacity = 1
          }, 500)
        }, 3000)
      }
    } else if (this.tab === 'clock') {
      if (this.selectedClockFeature === 'time') {
        // Display current time
        fullscreenContent.innerHTML = `
          <div class="text-9xl font-mono font-bold">
            ${this.currentTime}
          </div>
        `
        // Keep updating the time
        this.fullscreenClockInterval = setInterval(() => {
          fullscreenContent.innerHTML = `
            <div class="text-9xl font-mono font-bold">
              ${this.currentTime}
            </div>
          `
        }, 1000)
      } else if (this.selectedClockFeature === 'countdown') {
        // Start countdown
        if (!this.fullscreenTimerInterval) {
          this.countdownTotalSeconds =
            (parseInt(this.timerMinutes) || 0) * 60 +
            (parseInt(this.timerSeconds) || 0)

          const updateCountdownDisplay = () => {
            const minutes = Math.floor(this.countdownTotalSeconds / 60)
            const seconds = this.countdownTotalSeconds % 60
            const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`

            fullscreenContent.innerHTML = `
              <div class="text-9xl font-mono font-bold">
                ${timeString}
              </div>
            `
          }

          updateCountdownDisplay()

          this.fullscreenTimerInterval = setInterval(() => {
            this.countdownTotalSeconds--
            // Update main UI timer values
            this.timerMinutes = Math.floor(this.countdownTotalSeconds / 60)
              .toString()
              .padStart(2, '0')
            this.timerSeconds = (this.countdownTotalSeconds % 60)
              .toString()
              .padStart(2, '0')
            this.updateTimerFields() // Update input fields

            if (this.countdownTotalSeconds > 0) {
              if (
                screenfull.isFullscreen &&
                this.tab === 'clock' &&
                this.selectedClockFeature === 'countdown'
              ) {
                updateCountdownDisplay()
              }
            } else {
              // Timer reached zero - start color terrorita effect if in fullscreen on timer
              clearInterval(this.fullscreenTimerInterval)
              this.fullscreenTimerInterval = null
              if (
                screenfull.isFullscreen &&
                this.tab === 'clock' &&
                this.selectedClockFeature === 'countdown'
              ) {
                this.startColorTerrorita(fullscreenContent)
              }
            }
          }, 1000)
        } else {
          // Interval already running, just update display
          const minutes = Math.floor(this.countdownTotalSeconds / 60)
          const seconds = this.countdownTotalSeconds % 60
          const timeString = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`

          fullscreenContent.innerHTML = `
            <div class="text-9xl font-mono font-bold">
              ${timeString}
            </div>
          `
        }
      } else if (this.selectedClockFeature === 'stopwatch') {
        // Start stopwatch if not already running
        if (!this.stopwatchRunning) {
          this.startStopwatch()
        }

        const updateStopwatchDisplay = () => {
          fullscreenContent.innerHTML = `
            <div class="text-9xl font-mono font-bold">
              ${this.stopwatchTime}
            </div>
          `
        }

        updateStopwatchDisplay()

        // Keep updating the display
        this.fullscreenStopwatchInterval = setInterval(() => {
          if (
            screenfull.isFullscreen &&
            this.tab === 'clock' &&
            this.selectedClockFeature === 'stopwatch'
          ) {
            updateStopwatchDisplay()
          }
        }, 10)
      }
    }

    if (screenfull.isEnabled) {
      // Check state if event listener is already attached
      if (!this.eventListenersAdded) {
        fullscreenWrapper.addEventListener('click', () => {
          if (screenfull.isFullscreen) screenfull.exit()
        })

        screenfull.on('change', () => {
          if (screenfull.isFullscreen) {
            fullscreenWrapper.classList.remove('hidden')
            if (this.checkOverflow) this.checkOverflow()
            window.addEventListener('resize', () => {
              if (this.checkOverflow) this.checkOverflow()
            })
            window.addEventListener('keydown', this.exitFullscreenHandler)
          } else {
            if (this.checkOverflow)
              window.removeEventListener('resize', () => {
                if (this.checkOverflow) this.checkOverflow()
              })
            window.removeEventListener('keydown', this.exitFullscreenHandler)
            if (this.slideshowInterval) {
              clearInterval(this.slideshowInterval)
              this.slideshowInterval = null
            }
            if (this.fullscreenClockInterval) {
              clearInterval(this.fullscreenClockInterval)
              this.fullscreenClockInterval = null
            }
            // Keep timer running - don't clear fullscreenTimerInterval
            // Keep stopwatch running - don't clear fullscreenStopwatchInterval
            this.releaseWakeLock()
            fullscreenWrapper.classList.add('hidden')
            fullscreenContent.className = ''
            fullscreenContent.style.transition = ''
            fullscreenContent.innerHTML = ''

            this.focusVisibleInput()

            // Focus the active feature button on clock tab
            if (this.tab === 'clock') {
              setTimeout(() => {
                const buttonId = `${this.selectedClockFeature}-btn`
                const button = document.getElementById(buttonId)
                if (button) {
                  const originalTabIndex = button.tabIndex
                  button.tabIndex = 0
                  button.focus()
                  setTimeout(() => (button.tabIndex = originalTabIndex), 100)
                }
              }, 0)
            }
          }
        })

        this.eventListenersAdded = true
      }

      screenfull.request(fullscreenContent)
      this.acquireWakeLock()

      if (typeof umami !== 'undefined') {
        umami.track(`Went fullscreen with ${this.tab}`)
      }
    }
  },
}))

Alpine.start()

// To easily debug Alpine stores in the console
window.alpineData = function(storeName = 'system') {
  return JSON.parse(JSON.stringify({ ...Alpine.store(storeName) }))
}
